import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';

const STORAGE_KEY = 'operacoes:warehouse-selection';

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map((id) => String(id)) : null;
    } catch {
        return null;
    }
}

function writeStored(ids) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // ignore quota / restricted-context errors
    }
}

export function useWarehouseConfig() {
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // null = "todos" (default). Array de IDs string = subconjunto selecionado.
    const [selectedIds, setSelectedIds] = useState(() => readStored());
    const inFlight = useRef(false);

    useEffect(() => {
        if (inFlight.current) {
            return;
        }
        inFlight.current = true;
        setLoading(true);
        api.get('/stock/warehouses?limit=200')
            .then((resp) => {
                const list = Array.isArray(resp?.results) ? resp.results : [];
                setWarehouses(
                    list.map((w) => ({
                        id: String(w.id ?? w.reference_id ?? w.warehouse_id ?? ''),
                        name: w.name || w.descricao || `Depósito ${w.id || ''}`,
                    })),
                );
            })
            .catch((err) => {
                setError(err?.message || 'Erro ao carregar depósitos');
            })
            .finally(() => {
                setLoading(false);
                inFlight.current = false;
            });
    }, []);

    const setSelection = useCallback((ids) => {
        if (ids === null || ids === undefined) {
            setSelectedIds(null);
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch {
                // ignore
            }
            return;
        }
        const normalized = Array.from(new Set((ids || []).map((id) => String(id))));
        setSelectedIds(normalized);
        writeStored(normalized);
    }, []);

    const filterDepots = useCallback(
        (depots = []) => {
            if (!selectedIds) {
                return depots;
            }
            const set = new Set(selectedIds);
            return (depots || []).filter((d) => set.has(String(d.id)));
        },
        [selectedIds],
    );

    const aggregatedTotal = useCallback(
        (depots = []) => filterDepots(depots).reduce((sum, d) => sum + Number(d.qty || 0), 0),
        [filterDepots],
    );

    const isAllSelected = selectedIds === null;

    return useMemo(
        () => ({
            warehouses,
            loading,
            error,
            selectedIds,
            isAllSelected,
            setSelection,
            filterDepots,
            aggregatedTotal,
        }),
        [warehouses, loading, error, selectedIds, isAllSelected, setSelection, filterDepots, aggregatedTotal],
    );
}
